import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	UserCheck,
	Search,
	Eye,
	CheckCircle,
	XCircle,
	Clock,
	MapPin,
	Shield,
	Ban,
	CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

import { DoulaApplication } from '@/types/doulaApplication'

import styles from './Admin.module.css'
import { monthsSelectArray } from "@/utils/helpers";

export default function ApplicationReview() {
	const navigate = useNavigate();
	const [applications, setApplications] = useState<DoulaApplication[]>([]);
	const [filteredApplications, setFilteredApplications] = useState<
		DoulaApplication[]
	>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [sortBy, setSortBy] = useState("newest");
	const [activeTab, setActiveTab] = useState("pending");

	useEffect(() => {
		loadApplications();
	}, []);

	useEffect(() => {
		filterApplications();
	}, [applications, searchTerm, sortBy, activeTab]);

	const loadApplications = async () => {
		try {
			setLoading(true);

			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the edge function instead of direct database access
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-doula-applications`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						filter: {}, // No initial filters, we'll filter on frontend
						sort: {
							field: "created_at",
							direction: "desc",
						},
						pagination: {
							page: 1,
							limit: 1000, // Get all for now, we'll filter client-side
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to load applications");
			}

			if (!result.success) {
				throw new Error(result.error || "Failed to load applications");
			}

			// Process the data to match our interface
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const processedData = result.data.applications.map((app: any) => ({
				...app,
				certifications: app.doula_certifications || [],
			}));

			setApplications(processedData);
		} catch (error) {
			setApplications([]);
		} finally {
			setLoading(false);
		}
	};

	const filterApplications = () => {
		let filtered = applications;

		// Filter by status based on active tab
		filtered = filtered.filter((app) => app.status === activeTab);

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(app) =>
					app.users.full_name
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					app.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					app.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					app.location_city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					app.location_state?.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}

		// Sort applications
		filtered.sort((a, b) => {
			switch (sortBy) {
				case "newest":
					return (
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
					);
				case "oldest":
					return (
						new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
					);
				case "experience":
					return (b.years_experience || 0) - (a.years_experience || 0);
				case "name":
					return (a.users.full_name || "").localeCompare(
						b.users.full_name || "",
					);
				default:
					return 0;
			}
		});

		setFilteredApplications(filtered);
	};

	const updateApplicationStatus = async (
		applicationId: string,
		newStatus: "pending" | "active" | "suspended",
	) => {
		try {
			// Get current user session for auth
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				throw new Error("No active session");
			}

			// Call the edge function instead of direct database access
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-application`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						application_id: applicationId,
						updates: {
							profile: {
								status: newStatus,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to update application status");
			}

			if (!result.success) {
				throw new Error(result.error || "Failed to update application status");
			}

			// Update local state
			setApplications((prev) =>
				prev.map((app) =>
					app.id === applicationId
						? {
							...app,
							status: newStatus,
							updated_at: new Date().toISOString(),
						}
						: app,
				),
			);
		} catch (error) {
			// Handle error silently
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "pending":
				return (
					<Badge
						variant="secondary"
						className="bg-yellow-100 text-yellow-800 border-yellow-200"
					>
						<Clock className="w-3 h-3 mr-1" />
						Pending Review
					</Badge>
				);
			case "active":
				return (
					<Badge
						variant="default"
						className="bg-green-100 text-green-800 border-green-200"
					>
						<CheckCircle className="w-3 h-3 mr-1" />
						Active
					</Badge>
				);
			case "suspended":
				return (
					<Badge
						variant="destructive"
						className="bg-red-100 text-red-800 border-red-200"
					>
						<XCircle className="w-3 h-3 mr-1" />
						Suspended
					</Badge>
				);
			default:
				return <Badge variant="secondary">{status}</Badge>;
		}
	};

	const getTabCounts = () => {
		return {
			pending: applications.filter((app) => app.status === "pending").length,
			active: applications.filter((app) => app.status === "active").length,
			suspended: applications.filter((app) => app.status === "suspended")
				.length,
		};
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatExperience = (application: DoulaApplication) => {
		return `${application.years_experience} years experience`;
	};

	const formatLocation = (application: DoulaApplication) => {
		return `${application.location_city}, ${application.location_state}`;
	};

	const tabCounts = getTabCounts();

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">
						Loading doula applications...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="text-center mb-8">
						<h1 className="text-4xl font-bold text-gray-900 mb-4 font-karla">
							Doula Application Review
						</h1>
					</div>
				</motion.div>

				{/* Tabs */}
				<Tabs
					defaultValue="pending"
					value={activeTab}
					onValueChange={setActiveTab}
					className="space-y-6"
				>
					<div className="flex justify-center">
						<TabsList className={`${styles.tabButtons} grid w-fit grid-cols-3 border border-newTheme-lightGrey h-auto`}>
							<TabsTrigger
								value="pending"
								className={`${styles.tabButton} flex items-center gap-2 data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Clock className="w-4 h-4" />
								Pending ({tabCounts.pending})
							</TabsTrigger>
							<TabsTrigger
								value="active"
								className={`${styles.tabButton} flex items-center gap-2 data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<CheckCircle className="w-4 h-4" />
								Active ({tabCounts.active})
							</TabsTrigger>
							<TabsTrigger
								value="suspended"
								className={`${styles.tabButton} flex items-center gap-2 data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<XCircle className="w-4 h-4" />
								Suspended ({tabCounts.suspended})
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value={activeTab} className="space-y-6">
						{/* Search and Filters */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="glass-card border-0 shadow-lg p-6 rounded-2xl"
						>
							<div className="flex flex-col lg:flex-row gap-4">
								{/* Search */}
								<div className="relative flex-1">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
									<Input
										type="text"
										placeholder="Search applications by name, title, location, or bio..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10 h-12 text-lg bg-white/80 backdrop-blur-sm border-0 font-karla"
									/>
								</div>

								{/* Sort Filter */}
								<div className="lg:w-64 text-right">
									<select onChange={(e) => setSortBy(e.target.value)} className="h-12 px-2 rounded-lg text-lg border border-newTheme-lightGrey shadow-btn-shadow font-karla">
										<option value="newest">Newest First</option>
										<option value="oldest">Oldest First</option>
										<option value="experience">Most Experience</option>
										<option value="name">Name A-Z</option>
									</select>
								</div>
							</div>

							{/* Quick Stats */}
							<div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mt-6">
								<div className="text-center">
									<div className="text-2xl font-bold text-sage-600">
										{filteredApplications.length}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}{" "}
										Applications
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-orange">
										{Math.round(
											filteredApplications.reduce(
												(sum, app) => sum + (app.years_experience || 0),
												0,
											) / filteredApplications.length,
										) || 0}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										Avg Years Experience
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-lime">
										{filteredApplications.reduce(
											(sum, app) => sum + (app.packages?.length || 0),
											0,
										)}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										Total Packages
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-spruce">
										{filteredApplications.reduce(
											(sum, app) => sum + (app.certifications?.length || 0),
											0,
										)}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										Total Certifications
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-red-600">
										{filteredApplications.reduce(
											(sum, app) => sum + (app.excluded_providers?.length || 0),
											0,
										)}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										Excluded Providers
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-lightGrey">
										{Math.round(
											filteredApplications.reduce((sum, app) => {
												if (!app.months_available) return sum + 12; // Default: all months available
												return (
													sum +
													Object.values(app.months_available).filter(Boolean)
														.length
												);
											}, 0) / (filteredApplications.length || 1),
										)}
									</div>
									<div className="text-sm text-gray-600 font-karla">
										Avg Months Available
									</div>
								</div>
							</div>
						</motion.div>

						{/* Applications Grid */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="grid grid-cols-1 lg:grid-cols-2 gap-6"
						>
							{filteredApplications.length === 0 ? (
								<div className="col-span-full">
									<Card className="glass-card border-0 shadow-lg">
										<CardContent className="p-12 text-center">
											<UserCheck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
											<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
												No {activeTab} applications found
											</h3>
											<p className="text-gray-600 font-karla">
												{searchTerm
													? `No applications match "${searchTerm}". Try adjusting your search terms.`
													: `No ${activeTab} applications at this time.`}
											</p>
										</CardContent>
									</Card>
								</div>
							) : (
								filteredApplications.map((application, index) => (
									<motion.div
										key={application.id}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
									>
										<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group h-full">
											<CardContent className="p-6">
												{/* Header */}
												<div className="flex items-start justify-between mb-4">
													<div className="flex items-center space-x-3 flex-1 min-w-0">
														<div className="w-14 h-14 rounded-lg overflow-hidden bg-primary-100 flex items-center justify-center flex-shrink-0 border-2 border-primary-200">
															{application.users.avatar_url ? (
																<img
																	src={application.users.avatar_url}
																	alt={application.users.full_name}
																	className="w-full h-full object-cover"
																/>
															) : (
																<div className="w-full h-full bg-primary-100 flex items-center justify-center">
																	<Shield className="h-6 w-6 text-primary-600" />
																</div>
															)}
														</div>
														<div className="flex-1 min-w-0">
															<h3 className="font-semibold text-gray-900 font-karla text-lg truncate">
																{application.users.full_name}
															</h3>
															<p className="text-sm text-gray-600 font-karla truncate">
																{formatExperience(application)}
															</p>
															<p className="text-xs text-gray-500 font-karla">
																Applied {formatDate(application.created_at)}
															</p>
														</div>
													</div>
													{getStatusBadge(application.status)}
												</div>

												{/* Title */}
												<div className="mb-3">
													<h4 className="font-semibold text-gray-900 font-karla text-base">
														{application.title}
													</h4>
												</div>

												{/* Bio */}
												{application.bio && (
													<p className="text-gray-600 font-karla mb-4 line-clamp-3 text-sm leading-relaxed">
														{application.bio}
													</p>
												)}

												{/* Location */}
												<div className="flex items-center text-sm text-gray-600 mb-4">
													<MapPin className="h-4 w-4 mr-1 text-primary-600" />
													<span className="font-karla">
														{formatLocation(application)}
													</span>
												</div>

												{/* Packages */}
												{application.packages &&
													application.packages.length > 0 && (
														<div className="mb-4">
															<p className="text-sm font-semibold text-gray-900 font-karla mb-2">
																Packages ({application.packages.length})
															</p>
															<div className="space-y-2">
																{application.packages.slice(0, 2).map((pkg) => (
																	<div
																		key={pkg.id}
																		className="flex items-center justify-between text-sm"
																	>
																		<span className="font-karla truncate mr-2">
																			{pkg.name}
																		</span>
																		<span className="font-semibold text-black flex-shrink-0">
																			${pkg.price}
																		</span>
																	</div>
																))}
																{application.packages.length > 2 && (
																	<p className="text-xs text-gray-500 font-karla">
																		+{application.packages.length - 2} more
																		packages
																	</p>
																)}
															</div>
														</div>
													)}

												{/* Certifications */}
												{application.certifications &&
													application.certifications.length > 0 && (
														<div className="mb-4">
															<p className="text-sm font-semibold text-gray-900 font-karla mb-2">
																Certifications
															</p>
															<div className="flex flex-wrap gap-1">
																{application.certifications
																	.slice(0, 3)
																	.map((cert) => (
																		<Badge
																			key={cert.id}
																			variant="secondary"
																			className="text-xs bg-primary-100 text-primary-800 font-karla"
																		>
																			{cert.name}
																		</Badge>
																	))}
																{application.certifications.length > 3 && (
																	<Badge
																		variant="secondary"
																		className="text-xs bg-gray-100 text-gray-600 font-karla"
																	>
																		+{application.certifications.length - 3}{" "}
																		more
																	</Badge>
																)}
															</div>
														</div>
													)}

												{/* Excluded Providers */}
												{application.excluded_providers &&
													application.excluded_providers.length > 0 && (
														<div className="mb-4">
															<p className="text-sm font-semibold text-gray-900 font-karla mb-2 flex items-center">
																<Ban className="h-4 w-4 mr-1 text-red-600" />
																Excluded Providers (
																{application.excluded_providers.length})
															</p>
															<div className="space-y-2">
																{application.excluded_providers
																	.slice(0, 2)
																	.map((provider) => (
																		<div
																			key={provider.id}
																			className="bg-red-50 border border-red-200 rounded-lg p-2"
																		>
																			<div className="flex items-start justify-between">
																				<div className="flex-1 min-w-0">
																					<p className="text-sm font-medium text-red-900 font-karla truncate">
																						{provider.provider_name}
																					</p>
																					{provider.provider_formatted_address && (
																						<p className="text-xs text-red-700 font-karla flex items-center mt-1">
																							<MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
																							<span className="truncate">
																								{
																									provider.provider_formatted_address
																								}
																							</span>
																						</p>
																					)}
																					{provider.provider_types &&
																						provider.provider_types.length >
																						0 && (
																							<div className="flex flex-wrap gap-1 mt-1">
																								{provider.provider_types
																									.slice(0, 2)
																									.map((type) => (
																										<Badge
																											key={type}
																											variant="outline"
																											className="text-xs bg-red-100 text-red-800 border-red-300 font-karla"
																										>
																											{type.replace(/_/g, " ")}
																										</Badge>
																									))}
																							</div>
																						)}
																				</div>
																			</div>
																		</div>
																	))}
																{application.excluded_providers.length > 2 && (
																	<p className="text-xs text-red-600 font-karla bg-red-50 px-2 py-1 rounded">
																		+{application.excluded_providers.length - 2}{" "}
																		more excluded providers
																	</p>
																)}
															</div>
														</div>
													)}

												{/* Months Available */}
												{application.months_available && (
													<div className="mb-4">
														<p className="text-sm font-semibold text-gray-900 font-karla mb-2 flex items-center">
															<CalendarDays className="h-4 w-4 mr-1 text-newTheme-orange" />
															Months Available (
															{
																Object.values(
																	application.months_available,
																).filter(Boolean).length
															}
															/12)
														</p>
														<div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
															<div className="grid grid-cols-6 gap-2">
																{monthsSelectArray.map(({ key, short }) => {
																	const isAvailable =
																		application.months_available?.[
																		key as keyof typeof application.months_available
																		];
																	return (
																		<div
																			key={key}
																			className={`text-xs text-center py-1 px-2 rounded font-karla ${isAvailable
																				? "bg-newTheme-sky text-black border border-newTheme-lightGrey"
																				: "bg-newTheme-spruce text-white border border-newTheme-lightGrey"
																				}`}
																		>
																			{short}
																		</div>
																	);
																})}
															</div>
															{Object.values(
																application.months_available,
															).filter(Boolean).length < 6 && (
																	<p className="text-xs text-amber-700 font-karla mt-2 text-center">
																		Limited availability may impact bookings
																	</p>
																)}
														</div>
													</div>
												)}

												{/* Action Buttons */}
												<div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
													<Button
														onClick={() =>
															navigate(`/admin-applications/${application.id}`)
														}
														className="w-full bg-newTheme-lime text-black font-karla shadow-btn-shadow border border-newTheme-lightGrey"
													>
														<Eye className="h-4 w-4 mr-2" />
														Review Application
													</Button>

													{activeTab === "pending" && (
														<div className="grid grid-cols-2 gap-2">
															<Button
																onClick={(e) => {
																	e.stopPropagation();
																	updateApplicationStatus(
																		application.id,
																		"active",
																	);
																}}
																size="sm"
																className="bg-newTheme-spruce hover:bg-green-700 text-white font-karla"
															>
																<CheckCircle className="h-3 w-3 mr-1" />
																Approve
															</Button>
															<Button
																onClick={(e) => {
																	e.stopPropagation();
																	updateApplicationStatus(
																		application.id,
																		"suspended",
																	);
																}}
																size="sm"
																variant="destructive"
																className="font-karla shadow-btn-shadow border border-newTheme-lightGrey"
															>
																<XCircle className="h-3 w-3 mr-1" />
																Reject
															</Button>
														</div>
													)}

													{activeTab === "active" && (
														<Button
															onClick={(e) => {
																e.stopPropagation();
																updateApplicationStatus(
																	application.id,
																	"suspended",
																);
															}}
															size="sm"
															variant="destructive"
															className="w-full font-karla"
														>
															<XCircle className="h-3 w-3 mr-1" />
															Suspend Listing
														</Button>
													)}

													{activeTab === "suspended" && (
														<Button
															onClick={(e) => {
																e.stopPropagation();
																updateApplicationStatus(
																	application.id,
																	"active",
																);
															}}
															size="sm"
															className="w-full bg-green-600 hover:bg-green-700 text-white font-karla"
														>
															<CheckCircle className="h-3 w-3 mr-1" />
															Restore Listing
														</Button>
													)}
												</div>
											</CardContent>
										</Card>
									</motion.div>
								))
							)}
						</motion.div>
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}
